"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  TextField,
} from "@mui/material"
import { ArrowBack, Add, Warning } from "@mui/icons-material"
import { useNotification } from "../../contexts/NotificationContext"
import { useAuth } from "../../contexts/AuthContext"
import PageHeader from "../../components/common/PageHeader"
import StatusBadge from "../../components/common/StatusBadge"
import LoadingButton from "../../components/common/LoadingButton"
import SubscriptionChangePlan from "../../components/subscriptions/SubscriptionChangePlan"
import SubscriptionCancelDialog from "../../components/subscriptions/SubscriptionCancelDialog"
import {
  getSubscription,
  getSubscriptionHistory,
  pauseSubscription,
  resumeSubscription,
  getPlans,
  createSubscription,
} from "../../services/subscription.service"
import { getCustomer, reactivateCustomer } from "../../services/customer.service"
import { switchBillingFrequency, reactivateSubscription } from "../../services/subscription-lifecycle.service"
import { isStripeConfigured } from "../../config/stripe"

const CustomerSubscription = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const { user, selectedOrganization } = useAuth()
  const [customer, setCustomer] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [history, setHistory] = useState([])
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [masterPassword, setMasterPassword] = useState("")
  const [changeBillingCycleDialogOpen, setChangeBillingCycleDialogOpen] = useState(false)
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false)
  const [reactivateCustomerDialogOpen, setReactivateCustomerDialogOpen] = useState(false)
  const [formData, setFormData] = useState({
    planId: "",
    billingCycle: "monthly",
  })
  const [billingCycle, setBillingCycle] = useState("monthly")

  const organizationId = selectedOrganization?._id || user?.organizationId

  useEffect(() => {
    if (organizationId && id) {
      fetchData()
    }
  }, [organizationId, id])

  const fetchData = async () => {
    setLoading(true)
    try {
      // Fetch customer
      const customerResponse = await getCustomer(id)
      setCustomer(customerResponse.data)

      try {
        // Fetch subscription
        const subscriptionResponse = await getSubscription(id)
        if (subscriptionResponse.data && subscriptionResponse.data.planId && subscriptionResponse.data.planId.name) {
          setSubscription(subscriptionResponse.data)
        } else {
          console.warn("Invalid subscription data:", subscriptionResponse.data)
          setSubscription(null)
        }
      } catch (error) {
        console.error("Failed to fetch subscription:", error)
        setSubscription(null)
      }

      try {
        // Fetch history
        const historyResponse = await getSubscriptionHistory(id)
        if (historyResponse.data && Array.isArray(historyResponse.data)) {
          const validHistory = historyResponse.data.filter((item) => item && item.planId && item.planId.name)
          setHistory(validHistory)
        } else {
          setHistory([])
        }
      } catch (error) {
        console.error("Failed to fetch subscription history:", error)
        setHistory([])
      }

      try {
        // Fetch plans
        const plansResponse = await getPlans({ organizationId })
        if (plansResponse.data && Array.isArray(plansResponse.data)) {
          const validPlans = plansResponse.data.filter((plan) => plan && plan.name)
          setPlans(validPlans)
        } else {
          setPlans([])
        }
      } catch (error) {
        console.error("Failed to fetch plans:", error)
        setPlans([])
      }
    } catch (error) {
      console.error("Failed to fetch customer data:", error)
      if (!error.response || error.response.status !== 404) {
        showNotification("Failed to fetch subscription data", "error")
      }
    } finally {
      setLoading(false)
    }
  }

  const handlePause = async () => {
    setActionLoading(true)
    try {
      await pauseSubscription(subscription._id)
      showNotification("Subscription paused successfully", "success")
      fetchData()
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to pause subscription", "error")
    } finally {
      setActionLoading(false)
    }
  }

  const handleResume = async () => {
    setActionLoading(true)
    try {
      await resumeSubscription(subscription._id)
      showNotification("Subscription resumed successfully", "success")
      fetchData()
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to resume subscription", "error")
    } finally {
      setActionLoading(false)
    }
  }

  const handleCreateSubscription = async () => {
    if (!formData.planId) {
      showNotification("Please select a plan", "error")
      return
    }

    const targetPlan = plans.find(p => p._id === formData.planId)
    if (targetPlan && targetPlan.isPublic === false) {
      if (!masterPassword) {
        showNotification("Master password is required for private plans", "error")
        return
      }
    }

    setActionLoading(true)
    try {
      const result = await createSubscription({
        organizationId,
        customerId: id,
        planId: formData.planId,
        billingCycle: formData.billingCycle,
      }, masterPassword);
      
      // Check if payment is required (paymentInfo with clientSecret is present)
      if (result.paymentInfo && result.paymentInfo.clientSecret) {
        // Check if Stripe is configured
        if (!isStripeConfigured()) {
          showNotification("Payment configuration error. Please contact the administrator.", "error");
          console.error("Stripe public key is missing");
          setCreateDialogOpen(false);
          return;
        }

        // Redirect to Stripe checkout page
        showNotification("Redirecting to payment page...", "info");
        window.location.href = `/payment/checkout?client_secret=${result.paymentInfo.clientSecret}&subscription_id=${result.paymentInfo.subscriptionId}`;
        return;
      }
      
      showNotification("Subscription created successfully", "success")
      setCreateDialogOpen(false)
      fetchData()
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to create subscription", "error")
    } finally {
      setActionLoading(false)
    }
  }

  const handleReactivate = async () => {
    setActionLoading(true)
    try {
      await reactivateSubscription(subscription._id)
      showNotification("Subscription reactivated successfully", "success")
      setReactivateDialogOpen(false)
      fetchData()
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to reactivate subscription", "error")
    } finally {
      setActionLoading(false)
    }
  }

  const handleReactivateCustomer = async () => {
    setActionLoading(true)
    try {
      await reactivateCustomer(id)
      showNotification("Customer reactivated successfully", "success")
      setReactivateCustomerDialogOpen(false)
      fetchData()
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to reactivate customer", "error")
    } finally {
      setActionLoading(false)
    }
  }

  const handleChangeBillingCycle = async () => {
    if (!billingCycle || billingCycle === subscription.billingCycle) {
      setChangeBillingCycleDialogOpen(false)
      return
    }

    setActionLoading(true)
    try {
      await switchBillingFrequency(subscription._id, billingCycle)
      showNotification("Billing cycle changed successfully", "success")
      setChangeBillingCycleDialogOpen(false)
      fetchData()
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to change billing cycle", "error")
    } finally {
      setActionLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => {
      const next = { ...prev, [name]: value }

      if (name === "planId") {
        const targetPlan = plans.find((p) => p && p._id === value)
        if (targetPlan?.planType === "lifetime") {
          // Lifetime plans must always use the lifetime billing cycle
          next.billingCycle = "lifetime"
        } else {
          // For non-lifetime plans, ensure we keep a recurring cycle
          if (next.billingCycle === "lifetime" || !["monthly", "annual"].includes(next.billingCycle)) {
            next.billingCycle = "monthly"
          }
        }
      }

      return next
    })
  }

  const handlePlanChanged = () => {
    fetchData()
  }

  const handleCancelled = () => {
    fetchData()
  }

  const openCreateDialog = () => {
    if (customer && customer.status === "inactive") {
      showNotification(
        "Cannot create subscription for inactive customer. Please reactivate the customer first.",
        "error",
      )
      return
    }

    setFormData({ planId: "", billingCycle: "monthly" })
    setMasterPassword("")
    setCreateDialogOpen(true)
  }

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false)
    setMasterPassword("")
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  // Check if subscription is pending cancellation
  const isPendingCancellation =
    subscription?.status === "pending_cancellation" ||
    (subscription?.status === "active" && subscription?.cancelType === "end_of_cycle")

  // Safely get the subscription plan name
  const getSubscriptionPlanName = () => {
    if (subscription && subscription.planId && subscription.planId.name) {
      return subscription.planId.name
    }
    return "Unknown Plan"
  }

  // Safely render history items
  const renderHistoryItems = () => {
    if (!history || !Array.isArray(history) || history.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No subscription history available
        </Typography>
      )
    }

    return history
      .filter((item) => item && item.planId && item.planId.name)
      .map((item, index) => (
        <Box key={item._id} sx={{ mb: 2, pb: 2, borderBottom: index < history.length - 1 ? "1px solid #eee" : "none" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle2">{item.planId.name} Plan</Typography>
            <StatusBadge
              status={
                item.status === "active" && item.cancelType === "end_of_cycle" ? "pending_cancellation" : item.status
              }
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {new Date(item.startDate).toLocaleDateString()} -{" "}
            {item.endDate ? new Date(item.endDate).toLocaleDateString() : "Present"}
          </Typography>
          {item.cancelReason && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Cancellation reason: {item.cancelReason}
            </Typography>
          )}
        </Box>
      ))
  }

  // Safely render plan options
  const renderPlanOptions = () => {
    if (!plans || !Array.isArray(plans) || plans.length === 0) {
      return <MenuItem value="">No plans available</MenuItem>
    }

    return plans
      .filter((plan) => plan && plan.name)
      .map((plan) => (
        <MenuItem key={plan._id} value={plan._id}>
          {plan.name} - ${plan.monthlyPrice}/month or ${plan.annualPrice}/year
        </MenuItem>
      ))
  }

  return (
    <Box>
      <PageHeader
        title={customer ? `${customer.name}'s Subscription` : "Customer Subscription"}
        subtitle="Manage customer subscription"
        breadcrumbs={[
          { text: "Dashboard", link: "/dashboard" },
          { text: "Customers", link: "/customers" },
          { text: customer ? customer.name : "Customer", link: customer ? `/customers/${id}` : "/customers" },
          { text: "Subscription" },
        ]}
      />

      <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(`/customers/${id}`)} sx={{ mb: 3 }}>
        Back to Customer
      </Button>

      {customer && customer.status === "inactive" && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => setReactivateCustomerDialogOpen(true)}>
              Reactivate
            </Button>
          }
        >
          <Typography variant="subtitle2">This customer is inactive</Typography>
          <Typography variant="body2">
            Subscription management is disabled for inactive customers. Reactivate the customer to manage subscriptions.
          </Typography>
        </Alert>
      )}

      {!subscription ? (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            No Active Subscription
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            This customer doesn't have an active subscription. Create one to get started.
          </Typography>

          {customer && customer.status === "inactive" ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" color="error" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Warning fontSize="small" />
                Cannot create subscription for inactive customer
              </Typography>
              <Button variant="contained" color="primary" onClick={() => setReactivateCustomerDialogOpen(true)}>
                Reactivate Customer
              </Button>
            </Box>
          ) : (
            <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
              Create Subscription
            </Button>
          )}
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Current Subscription</Typography>
            <StatusBadge status={isPendingCancellation ? "pending_cancellation" : subscription.status} />
          </Box>
          <Divider sx={{ mb: 3 }} />

          {isPendingCancellation && (
            <Alert severity="info" sx={{ mb: 3 }}>
              This subscription has been scheduled to cancel at the end of the current billing period (
              {new Date(subscription.currentPeriodEnd).toLocaleDateString()}).
            </Alert>
          )}

          {subscription.pendingPlanId && (
            <Alert severity="info" sx={{ mb: 3 }}>
              This subscription has a pending plan change scheduled for{" "}
              {new Date(subscription.pendingPlanEffectiveDate).toLocaleDateString()}.
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Plan
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {getSubscriptionPlanName()}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Billing Cycle
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, textTransform: "capitalize" }}>
                {subscription.billingCycle}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                Price
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                ${subscription.price} {subscription.currency}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Current Period
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {new Date(subscription.currentPeriodStart).toLocaleDateString()} -{" "}
                {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
              </Typography>

              {subscription.trialEndDate && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    Trial Ends
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(subscription.trialEndDate).toLocaleDateString()}
                  </Typography>
                </>
              )}

              <Typography variant="subtitle2" color="text.secondary">
                Auto Renew
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {subscription.autoRenew ? "Yes" : "No"}
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
            {customer && customer.status === "inactive" ? (
              <Typography variant="body2" color="error" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Warning fontSize="small" />
                Subscription management is disabled for inactive customers
              </Typography>
            ) : (
              <>
                {/* Always show Change Plan button unless subscription is canceled or expired */}
                {subscription.status !== "canceled" && subscription.status !== "expired" && (
                  <Button variant="outlined" onClick={() => setChangePlanDialogOpen(true)}>
                    Change Plan
                  </Button>
                )}

                {subscription.status === "active" && !isPendingCancellation && subscription.billingCycle !== "lifetime" && (
                  <>
                    <Button variant="outlined" color="warning" onClick={handlePause} disabled={actionLoading}>
                      Pause Subscription
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setCancelDialogOpen(true)}
                      disabled={actionLoading}
                    >
                      Cancel Subscription
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setBillingCycle(subscription.billingCycle)
                        setChangeBillingCycleDialogOpen(true)
                      }}
                    >
                      Change Billing Cycle
                    </Button>
                  </>
                )}

                {subscription.status === "paused" && (
                  <Button variant="outlined" color="primary" onClick={handleResume} disabled={actionLoading}>
                    Resume Subscription
                  </Button>
                )}

                {isPendingCancellation && (
                  <Button variant="outlined" color="primary" onClick={handleResume} disabled={actionLoading}>
                    Cancel the Cancellation
                  </Button>
                )}

                {(subscription.status === "canceled" || subscription.status === "expired") && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setReactivateDialogOpen(true)}
                    disabled={actionLoading}
                  >
                    Reactivate Subscription
                  </Button>
                )}
              </>
            )}
          </Box>
        </Paper>
      )}

      {history.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Subscription History
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ maxHeight: 400, overflow: "auto" }}>{renderHistoryItems()}</Box>
        </Paper>
      )}

      {/* Create Subscription Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog}>
        <DialogTitle>Create Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select a plan and billing cycle for {customer?.name}'s subscription.
          </DialogContentText>

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel id="plan-label">Plan</InputLabel>
            <Select labelId="plan-label" name="planId" value={formData.planId} label="Plan" onChange={handleChange}>
              {renderPlanOptions()}
            </Select>
          </FormControl>

          {(() => {
            const targetPlan = plans.find(p => p && p._id === formData.planId)
            if (targetPlan?.planType === "lifetime") {
              return (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Billing Cycle
                  </Typography>
                  <Typography variant="body2">Lifetime (one-time payment)</Typography>
                </Box>
              )
            }

            return (
              <FormControl fullWidth>
                <InputLabel id="billing-cycle-label">Billing Cycle</InputLabel>
                <Select
                  labelId="billing-cycle-label"
                  name="billingCycle"
                  value={formData.billingCycle}
                  label="Billing Cycle"
                  onChange={handleChange}
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="annual">Annual</MenuItem>
                </Select>
              </FormControl>
            )
          })()}
          {(() => {
            const targetPlan = plans.find(p => p._id === formData.planId)
            if (targetPlan && targetPlan.isPublic === false) {
              return (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This is a private plan. Please enter the master password to proceed.
                  </Alert>
                  <TextField
                    fullWidth
                    type="password"
                    label="Master Password"
                    variant="outlined"
                    value={masterPassword}
                    onChange={(e) => setMasterPassword(e.target.value)}
                  />
                </Box>
              )
            }
            return null
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <LoadingButton onClick={handleCreateSubscription} loading={actionLoading} variant="contained">
            Create Subscription
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Change Plan Dialog */}
      {subscription && (
        <SubscriptionChangePlan
          open={changePlanDialogOpen}
          onClose={() => setChangePlanDialogOpen(false)}
          subscription={subscription}
          onPlanChanged={handlePlanChanged}
          organizationId={organizationId}
        />
      )}

      {/* Cancel Subscription Dialog */}
      {subscription && (
        <SubscriptionCancelDialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
          subscription={subscription}
          onCancelled={handleCancelled}
        />
      )}

      {/* Change Billing Cycle Dialog */}
      <Dialog open={changeBillingCycleDialogOpen} onClose={() => setChangeBillingCycleDialogOpen(false)}>
        <DialogTitle>Change Billing Cycle</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select a new billing cycle for {customer?.name}'s subscription.
          </DialogContentText>

          <Alert severity="info" sx={{ mb: 2 }}>
            Switching to a longer billing cycle (e.g., monthly to annual) takes effect immediately with prorated
            billing. Switching to a shorter cycle takes effect at the next renewal date.
          </Alert>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="billing-cycle-change-label">Billing Cycle</InputLabel>
            <Select
              labelId="billing-cycle-change-label"
              value={billingCycle}
              label="Billing Cycle"
              onChange={(e) => setBillingCycle(e.target.value)}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="annual">Annual</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangeBillingCycleDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            onClick={handleChangeBillingCycle}
            loading={actionLoading}
            variant="contained"
            disabled={billingCycle === subscription?.billingCycle}
          >
            Change Billing Cycle
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Reactivate Subscription Dialog */}
      <Dialog open={reactivateDialogOpen} onClose={() => setReactivateDialogOpen(false)}>
        <DialogTitle>Reactivate Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reactivate this subscription? The customer will be billed immediately for a new
            billing period.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReactivateDialogOpen(false)}>Cancel</Button>
          <LoadingButton onClick={handleReactivate} loading={actionLoading} variant="contained">
            Reactivate Subscription
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Reactivate Customer Dialog */}
      <Dialog open={reactivateCustomerDialogOpen} onClose={() => setReactivateCustomerDialogOpen(false)}>
        <DialogTitle>Reactivate Customer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reactivate this customer? This will allow you to manage subscriptions and credits
            for this customer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReactivateCustomerDialogOpen(false)}>Cancel</Button>
          <LoadingButton onClick={handleReactivateCustomer} loading={actionLoading} variant="contained" color="success">
            Reactivate Customer
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CustomerSubscription

